'use client'

import posthog from 'posthog-js'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useCallback, useRef } from 'react'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import useGeneralStore from '@/lib/hooks/zustand-stores/general-store'
import { useLogger } from '@/lib/hooks/useLogger'

export function usePostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const user = useUserStore(state => state.user)
  const isMobile = useGeneralStore(state => state.isMobile)
  const logger = useLogger()
  const lastPathRef = useRef(pathname)
  const isFirstView = useRef(true)

  const capturePageView = useCallback(() => {
    try {
      if (!pathname) return

      // Only skip if it's the same path and not the first view
      if (lastPathRef.current === pathname && !isFirstView.current) return

      let url = window.origin + pathname
      if (searchParams?.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      
      logger.debug('Capturing PageView:', { pathname, url })
      
      // Capture pageview
      posthog.capture('$pageview', {
        $current_url: url,
        $pathname: pathname,
        device_type: isMobile ? 'mobile' : 'desktop',
        user_id: user?._id,
        referrer: document.referrer,
        navigation_type: getNavigationType(pathname),
        search_params: Object.fromEntries(searchParams?.entries() || []),
        timestamp: new Date().toISOString(),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        performance_navigation_type: performance?.navigation?.type,
        page_load_time: performance?.now(),
      })

      // Capture page leave for the previous page
      if (lastPathRef.current && !isFirstView.current) {
        posthog.capture('$pageleave', {
          $previous_url: window.origin + lastPathRef.current,
          $current_url: url,
          duration_ms: performance?.now(),
          user_id: user?._id
        })
      }

      lastPathRef.current = pathname
      isFirstView.current = false
    } catch (error) {
      logger.error('Failed to capture pageview:', error)
    }
  }, [pathname, searchParams, user, isMobile, logger])

  useEffect(() => {
    // Capture initial pageview after a short delay to ensure context is loaded
    const timeoutId = setTimeout(capturePageView, 100)
    
    // Cleanup function to handle page leave
    return () => {
      clearTimeout(timeoutId)
      if (pathname && !isFirstView.current) {
        try {
          posthog.capture('$pageleave', {
            $current_url: window.origin + pathname,
            duration_ms: performance?.now(),
            user_id: user?._id
          })
        } catch (error) {
          logger.error('Failed to capture pageleave:', error)
        }
      }
    }
  }, [capturePageView, pathname, user?._id, logger])
}

function getNavigationType(pathname: string): string {
  const pathMap: Record<string, string> = {
    '/chat': 'chat',
    '/profile': 'profile',
    '/feed': 'feed',
    '/settings': 'settings',
    '/matches': 'matches'
  }

  return Object.entries(pathMap).find(([path]) => pathname.startsWith(path))?.[1] || 'other'
}

// Optional: Export a hook for manual event tracking
export function usePostHogEvent() {
  const logger = useLogger()

  return useCallback((eventName: string, properties?: Record<string, any>) => {
    try {
      logger.debug('Capturing event:', { eventName, properties })
      posthog.capture(eventName, properties)
    } catch (error) {
      logger.error('Failed to capture event:', error)
    }
  }, [logger])
} 